import React from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const cleanDate = dateStr.split('T')[0];
    const parts = cleanDate.split('-');
    if (parts.length === 3) {
        const [year, month, day] = parts;
        return `${day}/${month}/${year}`;
    }
    return dateStr;
};

const MultiGridFullscreenModal = ({
    isMultiGridFullscreen,
    setIsMultiGridFullscreen,
    multiGridCount,
    setMultiGridCount,
    pnlUnit,
    setPnlUnit,
    multiGridChartData,
    multiPanels,
    setMultiPanels,
    equityChartData,
    baselineScope,
    setBaselineScope,
    filterOnBaseline,
    setFilterOnBaseline,
    setSelectedFSR,
    setSelectedMABits,
    setSelectedCEPE
}) => {
    if (!isMultiGridFullscreen) return null;

    const toggleGlobalFSR = (key) => {
        const activePanels = multiPanels.slice(0, multiGridCount);
        const allHave = activePanels.every(p => (p.selectedFSR || ['R', 'S', 'F']).includes(key));
        setMultiPanels(prev => prev.map((p, i) => {
            if (i >= multiGridCount) return p;
            const cur = p.selectedFSR || ['R', 'S', 'F'];
            const next = allHave ? cur.filter(x => x !== key) : Array.from(new Set([...cur, key]));
            return { ...p, selectedFSR: next };
        }));
        if (setSelectedFSR) {
            setSelectedFSR(prev => allHave ? prev.filter(x => x !== key) : Array.from(new Set([...prev, key])));
        }
    };

    const toggleGlobalMABits = (bit) => {
        const activePanels = multiPanels.slice(0, multiGridCount);
        const allHave = activePanels.every(p => (p.selectedMABits || ['00', '01', '10', '11']).includes(bit));
        setMultiPanels(prev => prev.map((p, i) => {
            if (i >= multiGridCount) return p;
            const cur = p.selectedMABits || ['00', '01', '10', '11'];
            const next = allHave ? cur.filter(x => x !== bit) : Array.from(new Set([...cur, bit]));
            return { ...p, selectedMABits: next };
        }));
        if (setSelectedMABits) {
            setSelectedMABits(prev => allHave ? prev.filter(x => x !== bit) : Array.from(new Set([...prev, bit])));
        }
    };

    const toggleGlobalCEPE = (opt) => {
        const activePanels = multiPanels.slice(0, multiGridCount);
        const allHave = activePanels.every(p => (p.selectedCEPE || ['CE', 'PE']).includes(opt));
        setMultiPanels(prev => prev.map((p, i) => {
            if (i >= multiGridCount) return p;
            const cur = p.selectedCEPE || ['CE', 'PE'];
            const next = allHave ? cur.filter(x => x !== opt) : Array.from(new Set([...cur, opt]));
            return { ...p, selectedCEPE: next };
        }));
        if (setSelectedCEPE) {
            setSelectedCEPE(prev => allHave ? prev.filter(x => x !== opt) : Array.from(new Set([...prev, opt])));
        }
    };

    const resetGlobalFilters = () => {
        setMultiPanels(prev => prev.map((p, i) => {
            if (i >= multiGridCount) return p;
            return {
                ...p,
                selectedFSR: ['R', 'S', 'F'],
                selectedMABits: ['00', '01', '10', '11'],
                selectedCEPE: ['CE', 'PE']
            };
        }));
        if (setSelectedFSR) setSelectedFSR(['R', 'S', 'F']);
        if (setSelectedMABits) setSelectedMABits(['00', '01', '10', '11']);
        if (setSelectedCEPE) setSelectedCEPE(['CE', 'PE']);
    };

    return (
        <div className="fixed inset-0 z-[99999] bg-[#0b0c10] p-6 flex flex-col min-h-0 w-screen h-screen overflow-hidden">
            {/* Modal Header */}
            <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-gray-800/80 shrink-0 bg-[#12141d] px-5 py-3 rounded-xl border border-gray-800 shadow-xl">
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2.5">
                        <span className="text-xl">⛶</span>
                        <h2 className="text-lg font-black text-white tracking-wide">
                            Multi-Chart Comparison Workspace
                        </h2>
                    </div>
                    <span className="px-2.5 py-1 rounded-full bg-purple-500/20 text-purple-300 text-xs font-mono font-bold border border-purple-500/30 flex items-center gap-1.5 shadow-sm">
                        <span className="w-2 h-2 rounded-full bg-purple-400 animate-ping" />
                        {multiGridCount} Side-by-Side Strategy Panels
                    </span>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-1.5 bg-[#1a1d29] p-1 rounded-lg border border-gray-700/80">
                        <span className="text-[10px] text-gray-400 font-extrabold uppercase px-1.5">Grid Layout:</span>
                        {[2, 4, 6].map(count => (
                            <button
                                key={count}
                                onClick={() => setMultiGridCount(count)}
                                className={`px-3 py-1 rounded text-xs font-extrabold transition-all cursor-pointer ${
                                    multiGridCount === count ? 'bg-purple-600 text-white shadow-md' : 'text-gray-400 hover:text-gray-200'
                                }`}
                            >
                                {count} Panels
                            </button>
                        ))}
                    </div>

                    {/* Baseline Scope Toggle */}
                    {setBaselineScope && (
                        <button
                            onClick={() => setBaselineScope(prev => prev === 'all' ? 'selected' : 'all')}
                            className={`px-3 py-1.5 rounded-lg text-xs font-extrabold transition-all border cursor-pointer ${
                                baselineScope === 'all'
                                    ? 'bg-purple-600/40 border-purple-400 text-purple-200 font-semibold shadow-md'
                                    : 'bg-[#1a1d29] border-gray-700 text-gray-300 hover:text-white'
                            }`}
                            title="Toggle Cyan Line Baseline: Compare against All Strategies Global Portfolio (ON) vs Selected Panel Strategy Only (OFF)"
                        >
                            {baselineScope === 'all' ? '🌐 Baseline: All Strategies' : '🎯 Baseline: Selected Strategy'}
                        </button>
                    )}

                    {/* Filter on Baseline Toggle */}
                    {setFilterOnBaseline && (
                        <button
                            onClick={() => setFilterOnBaseline(prev => !prev)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-extrabold transition-all border cursor-pointer ${
                                filterOnBaseline
                                    ? 'bg-amber-500/20 border-amber-500/50 text-amber-300 font-semibold shadow-md'
                                    : 'bg-[#1a1d29] border-gray-700 text-gray-400 hover:text-white'
                            }`}
                            title="Apply active panel filters to Cyan Baseline Line (OFF = Raw Baseline, ON = Filtered Baseline)"
                        >
                            {filterOnBaseline ? 'Filter Baseline: ON' : 'Filter Baseline: OFF'}
                        </button>
                    )}

                    <div className="flex bg-[#1a1d29] p-1 rounded-lg border border-gray-700/80 gap-1">
                        <button
                            onClick={() => setPnlUnit('inr')}
                            className={`px-3 py-1 rounded text-xs font-extrabold transition-all cursor-pointer ${
                                pnlUnit === 'inr' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-400 hover:text-gray-200'
                            }`}
                        >
                            ₹ INR
                        </button>
                        <button
                            onClick={() => setPnlUnit('pct')}
                            className={`px-3 py-1 rounded text-xs font-extrabold transition-all cursor-pointer ${
                                pnlUnit === 'pct' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-400 hover:text-gray-200'
                            }`}
                        >
                            % Percent
                        </button>
                    </div>

                    <button
                        onClick={() => setIsMultiGridFullscreen(false)}
                        className="px-4 py-1.5 rounded-lg bg-rose-500/20 hover:bg-rose-500/40 border border-rose-500/50 text-rose-200 text-xs font-extrabold flex items-center gap-2 transition-all cursor-pointer shadow-md"
                    >
                        <span className="text-sm font-black">✕</span> Exit Fullscreen
                    </button>
                </div>
            </div>

            {/* Global Filters Control Bar */}
            <div className="flex flex-wrap items-center gap-3 bg-[#12141d] px-4 py-2 mt-3 rounded-xl border border-gray-800 shrink-0 shadow-lg">
                <span className="text-[11px] font-black text-purple-400 uppercase tracking-wider flex items-center gap-1.5">
                    ⚡ Global Filters (Applies to All {multiGridCount} Panels):
                </span>

                <div className="h-4 w-px bg-gray-800" />

                {/* Global FSR */}
                <div className="flex items-center gap-1 text-[11px]">
                    <span className="text-gray-400 font-bold uppercase text-[10px] tracking-wider">FSR:</span>
                    {['R', 'S', 'F'].map(k => {
                        const activePanels = multiPanels.slice(0, multiGridCount);
                        const allHave = activePanels.every(p => (p.selectedFSR || ['R', 'S', 'F']).includes(k));
                        return (
                            <button
                                key={k}
                                onClick={() => toggleGlobalFSR(k)}
                                className={`px-2 py-0.5 rounded text-[11px] font-bold border transition-all cursor-pointer ${
                                    allHave ? 'bg-amber-500/20 text-amber-300 border-amber-500/50 shadow-sm' : 'bg-transparent text-gray-500 border-gray-800 hover:text-gray-300'
                                }`}
                                title={`Toggle FSR '${k}' across all ${multiGridCount} grid panels`}
                            >
                                {k}
                            </button>
                        );
                    })}
                </div>

                <div className="h-4 w-px bg-gray-800" />

                {/* Global MA Bits */}
                <div className="flex items-center gap-1 text-[11px]">
                    <span className="text-gray-400 font-bold uppercase text-[10px] tracking-wider">MA:</span>
                    {['00', '01', '10', '11'].map(bit => {
                        const activePanels = multiPanels.slice(0, multiGridCount);
                        const allHave = activePanels.every(p => (p.selectedMABits || ['00', '01', '10', '11']).includes(bit));
                        return (
                            <button
                                key={bit}
                                onClick={() => toggleGlobalMABits(bit)}
                                className={`px-1.5 py-0.5 rounded font-mono text-[11px] font-bold border transition-all cursor-pointer ${
                                    allHave ? 'bg-blue-600/30 text-blue-300 border-blue-500/50 shadow-sm' : 'bg-transparent text-gray-500 border-gray-800 hover:text-gray-300'
                                }`}
                                title={`Toggle MA bit '${bit}' across all ${multiGridCount} grid panels`}
                            >
                                {bit}
                            </button>
                        );
                    })}
                </div>

                <div className="h-4 w-px bg-gray-800" />

                {/* Global CE/PE */}
                <div className="flex items-center gap-1 text-[11px]">
                    <span className="text-gray-400 font-bold uppercase text-[10px] tracking-wider">Option:</span>
                    {['CE', 'PE'].map(t => {
                        const activePanels = multiPanels.slice(0, multiGridCount);
                        const allHave = activePanels.every(p => (p.selectedCEPE || ['CE', 'PE']).includes(t));
                        return (
                            <button
                                key={t}
                                onClick={() => toggleGlobalCEPE(t)}
                                className={`px-2 py-0.5 rounded text-[11px] font-bold border transition-all cursor-pointer ${
                                    allHave ? 'bg-purple-600/30 text-purple-300 border-purple-500/50 shadow-sm' : 'bg-transparent text-gray-500 border-gray-800 hover:text-gray-300'
                                }`}
                                title={`Toggle option type '${t}' across all ${multiGridCount} grid panels`}
                            >
                                {t}
                            </button>
                        );
                    })}
                </div>

                <button
                    onClick={resetGlobalFilters}
                    className="ml-auto px-2.5 py-1 rounded text-[10px] font-bold bg-gray-800/80 hover:bg-gray-700 text-gray-300 border border-gray-700 transition-all cursor-pointer"
                    title="Reset FSR, MA, and Option filters for all grid panels"
                >
                    Reset All Global Filters
                </button>
            </div>

            {/* Modal Grid Body */}
            <div className="flex-1 min-h-0 w-full pt-4">
                <div className={`grid gap-4 w-full h-full min-h-0 overflow-y-auto p-1 ${
                    multiGridCount === 2 ? 'grid-cols-1 md:grid-cols-2' :
                    multiGridCount === 4 ? 'grid-cols-1 md:grid-cols-2' :
                    'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
                }`}>
                    {multiGridChartData.map((pData, idx) => {
                        const panelConfig = multiPanels[idx] || {};
                        const pFSR = panelConfig.selectedFSR || ['R', 'S', 'F'];
                        const pMABits = panelConfig.selectedMABits || ['00', '01', '10', '11'];
                        const pCEPE = panelConfig.selectedCEPE || ['CE', 'PE'];

                        return (
                            <div key={idx} className="bg-[#14161f] border border-gray-800 rounded-xl p-3 flex flex-col h-full min-h-[300px] relative shadow-lg">
                                <div className="flex items-center justify-between gap-2 border-b border-gray-800 pb-2 mb-2 shrink-0">
                                    <div className="flex items-center gap-2">
                                        <span className="px-2 py-0.5 rounded bg-purple-500/20 text-purple-300 text-xs font-mono font-extrabold border border-purple-500/30">
                                            #{idx + 1}
                                        </span>
                                        <select
                                            value={panelConfig.strategy || pData.strategy || 'ALL'}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setMultiPanels(prev => prev.map((p, i) => i === idx ? { ...p, strategy: val } : p));
                                            }}
                                            className="bg-[#1a1d29] border border-gray-700 rounded px-2.5 py-1 text-xs font-bold text-gray-100 focus:outline-none max-w-[200px] truncate cursor-pointer"
                                        >
                                            <option value="ALL">🌐 All Strategies Portfolio</option>
                                            {(equityChartData.seriesList || []).map(s => (
                                                <option key={s} value={s}>{s}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="flex items-center gap-2 text-xs font-mono font-bold">
                                        <span className="text-gray-300" title="Whole Baseline">
                                            Base: {pnlUnit === 'inr' ? '₹' + Math.round(pData.wholeEndEquity || 0).toLocaleString('en-IN') : (pData.wholeEndEquity >= 0 ? '+' : '') + (pData.wholeEndEquity || 0).toFixed(1) + '%'}
                                        </span>
                                        <span className="text-emerald-400" title="Live Grid Filtered">
                                            Live: {pnlUnit === 'inr' ? '₹' + Math.round(pData.filteredEndEquity || 0).toLocaleString('en-IN') : (pData.filteredEndEquity >= 0 ? '+' : '') + (pData.filteredEndEquity || 0).toFixed(1) + '%'}
                                        </span>
                                    </div>
                                </div>

                                <div className="flex flex-wrap items-center justify-between gap-1 mb-2 text-[10px] shrink-0">
                                    <div className="flex items-center gap-1">
                                        <span className="text-gray-500 font-bold">FSR:</span>
                                        {['R', 'S', 'F'].map(k => {
                                            const isSel = pFSR.includes(k);
                                            return (
                                                <button
                                                    key={k}
                                                    onClick={() => {
                                                        const next = isSel ? pFSR.filter(x => x !== k) : [...pFSR, k];
                                                        setMultiPanels(prev => prev.map((p, i) => i === idx ? { ...p, selectedFSR: next } : p));
                                                    }}
                                                    className={`px-2 py-0.5 rounded font-bold border transition-all ${isSel ? 'bg-gray-800 text-amber-400 border-gray-600' : 'bg-transparent text-gray-600 border-gray-800'}`}
                                                >
                                                    {k}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    <div className="flex items-center gap-1">
                                        <span className="text-gray-500 font-bold">MA:</span>
                                        {['00', '01', '10', '11'].map(bit => {
                                            const isSel = pMABits.includes(bit);
                                            return (
                                                <button
                                                    key={bit}
                                                    onClick={() => {
                                                        const next = isSel ? pMABits.filter(x => x !== bit) : [...pMABits, bit];
                                                        setMultiPanels(prev => prev.map((p, i) => i === idx ? { ...p, selectedMABits: next } : p));
                                                    }}
                                                    className={`px-1.5 py-0.5 rounded font-mono font-bold border transition-all ${isSel ? 'bg-blue-900/40 text-blue-300 border-blue-600' : 'bg-transparent text-gray-600 border-gray-800'}`}
                                                >
                                                    {bit}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    <div className="flex items-center gap-1">
                                        <span className="text-gray-500 font-bold">OPT:</span>
                                        {['CE', 'PE'].map(t => {
                                            const isSel = pCEPE.includes(t);
                                            return (
                                                <button
                                                    key={t}
                                                    onClick={() => {
                                                        const next = isSel ? pCEPE.filter(x => x !== t) : [...pCEPE, t];
                                                        setMultiPanels(prev => prev.map((p, i) => i === idx ? { ...p, selectedCEPE: next } : p));
                                                    }}
                                                    className={`px-1.5 py-0.5 rounded font-bold border transition-all ${isSel ? 'bg-purple-900/40 text-purple-300 border-purple-600' : 'bg-transparent text-gray-600 border-gray-800'}`}
                                                >
                                                    {t}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    <button
                                        onClick={() => {
                                            const cur = panelConfig.filterOnBaseline !== undefined ? panelConfig.filterOnBaseline : filterOnBaseline;
                                            setMultiPanels(prev => prev.map((p, i) => i === idx ? { ...p, filterOnBaseline: !cur } : p));
                                        }}
                                        className={`px-1.5 py-0.5 rounded font-bold text-[9px] border transition-all cursor-pointer ${
                                            (panelConfig.filterOnBaseline !== undefined ? panelConfig.filterOnBaseline : filterOnBaseline)
                                                ? 'bg-amber-500/20 text-amber-300 border-amber-500/40 font-bold'
                                                : 'bg-transparent text-gray-500 border-gray-800 hover:text-gray-300'
                                        }`}
                                        title="Toggle filter on Baseline for this panel"
                                    >
                                        Base Filter: {(panelConfig.filterOnBaseline !== undefined ? panelConfig.filterOnBaseline : filterOnBaseline) ? 'ON' : 'OFF'}
                                    </button>
                                </div>

                                <div className="flex-1 min-h-0 w-full">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={pData.data} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" opacity={0.2} vertical={false} />
                                            <XAxis dataKey="date" stroke="#4B5563" tick={{ fill: '#9CA3AF' }} fontSize={10} tickLine={false} tickFormatter={formatDate} />
                                            <YAxis stroke="#4B5563" tick={{ fill: '#9CA3AF' }} fontSize={10} tickLine={false} tickFormatter={(v) => pnlUnit === 'inr' ? `₹${(v/1000).toFixed(0)}k` : `${v.toFixed(0)}%`} />
                                            <Tooltip
                                                contentStyle={{ backgroundColor: '#1f2937', borderColor: '#374151', borderRadius: '0.5rem', fontSize: '11px' }}
                                                itemStyle={{ color: '#f3f4f6' }}
                                                labelFormatter={formatDate}
                                            />
                                            <Line type="monotone" dataKey="Whole Baseline" stroke="#FFFFFF" dot={false} strokeWidth={2.0} name="Whole Baseline" />
                                            <Line type="monotone" dataKey="Live Grid Filtered" stroke="#10B981" dot={false} strokeWidth={2.2} name="Live Grid Filtered" />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default MultiGridFullscreenModal;
